import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { CheckCircleBroken, Loading02, Hash02 } from '@untitled-ui/icons-react';
import dayjs from 'dayjs';
import { ChangeEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconContainer2, IconDamagedContainer, IconPlus, IconSchedule, IconSearch, IconX, IconXCircle } from '@/components/icons';
import BaseSkeleton from '@/components/ui/BaseSkeleton';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { useLocale } from '@/hooks/use-locale';
import { useUserInfoStore } from '@/store';
import { IHelpdeskTicket } from '@/types/helpdesk';
import { cn } from '@/utils/cn';

import { useGetContracts } from '../../contracts/api/use-get-contracts';
import { useGetHelpdeskTicketTypes } from '../api/use-get-helpdesk-ticket-types';
import { useGetHelpdeskTickets } from '../api/use-get-helpdesk-tickets';
import Chat from '../components/Chat';
import CreateTicketForm from '../components/CreateTicketForm';
import { FAQ } from '../utils/faq';
import { ticketStatuses } from '../utils/ticket-status';

const CommunicationPage = () => {
  const { code: ownerCode } = useUserInfoStore((state) => state.selectedAccount);
  const { t } = useTranslation('communicationPage');
  const [searchValue, setSearchValue] = useState('');
  const [selectedContractNumber, setSelectedContractNumber] = useState<string>();
  const [selectedStatus, setSelectedStatus] = useState<number>();
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState<number>();
  const [selectedTicket, setSelectedTicket] = useState<IHelpdeskTicket>();
  const [isTicketCreationModeEnabled, setIsTicketCreationModeEnabled] = useState(false);

  const { currentLocale } = useLocale();

  const helpdeskLocalesDict = {
    et: 'et_EE',
    ru: 'ru_RU',
    en: 'en_EN',
  };

  const helpdeskLang = helpdeskLocalesDict[currentLocale as 'ru' | 'en' | 'et'];

  const handleCreateRequestButtonClick = () => {
    setIsTicketCreationModeEnabled(true);
  };

  const handleBackFromTicketFormClick = () => {
    setIsTicketCreationModeEnabled(false);
  };

  const handleSearchValueChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchValue(event.currentTarget.value);
  };

  const handleSelectContractNumber = (contractNumber: string) => {
    setSelectedContractNumber(contractNumber);
  };

  const handleSelectedTicketTypeIdChange = (ticketTypeId: string) => {
    setSelectedTicketTypeId(Number(ticketTypeId));
  };

  const handleSelectedStatusChange = (status: string) => {
    setSelectedStatus(Number(status));
  };

  const handleClearButtonClick = () => {
    setSearchValue('');
  };

  const handleTicketClick = (ticket: IHelpdeskTicket) => {
    setSelectedTicket(ticket);
    setIsTicketCreationModeEnabled(false);
  };

  const contractsQuery = useGetContracts<Contract[]>({
    disablePagination: true,
    ownerCodes: [ownerCode],
  });

  const ticketsQuery = useGetHelpdeskTickets({
    stageId: selectedStatus,
    ticketTypeId: selectedTicketTypeId,
    contractNumbers: selectedContractNumber,
  });
  const ticketTypesQuery = useGetHelpdeskTicketTypes({ lang: helpdeskLang });

  const hasFilters = selectedContractNumber || selectedStatus || selectedTicketTypeId;

  const handleClearAllButtonClick = () => {
    setSelectedContractNumber(null);
    setSelectedStatus(null);
    setSelectedTicketTypeId(null);
  };

  return (
    <div className="flex md:flex-row flex-col gap-5 sm:px-4 md:px-6 lg:px-8 p-0 pt-5 h-full">
      <div
        className={cn(
          'flex-col md:flex-1 bg-white p-6 rounded-[32px] md:rounded-t-[32px] h-[648px] md:h-auto',
          selectedTicket || isTicketCreationModeEnabled ? 'hidden md:flex' : 'flex',
        )}
      >
        <div className="flex sm:flex-row flex-col justify-between sm:items-center gap-4 sm:gap-6 mb-6">
          <span className="font-bold sm:text-2xl md:text-[36px]">{t('helpCenter')}</span>
          <button
            onClick={handleCreateRequestButtonClick}
            className="flex items-center gap-2 bg-brand-green px-6 rounded-2xl h-10 sm:h-12 font-medium text-gray-10 stroke-gray-10"
          >
            <IconPlus className="w-6 h-6" />
            <span>{t('addNewRequest')}</span>
          </button>
        </div>
        <Input
          parentClassName="w-full mb-4"
          placeholder={t('searchRequest')}
          value={searchValue}
          onChange={handleSearchValueChange}
          leftSide={
            <span className="text-transparent stroke-brand-brown">
              <IconSearch size="sm" />
            </span>
          }
          rightSide={
            <button className={cn('stroke-black', searchValue ? 'flex' : 'hidden')} onClick={handleClearButtonClick}>
              <IconX size="xs" />
            </button>
          }
        />
        <div className="flex justify-between items-center">
          <span className="font-normal text-gray-10 text-sm sm:text-xl">{t('filter', { ns: 'common' })}</span>
          <button
            onClick={handleClearAllButtonClick}
            disabled={!hasFilters}
            className={cn('font-normal text-xs sm:text-sm', hasFilters ? 'text-[#007AFF]' : 'text-[#B9B9B9]')}
          >
            {t('clearAll')}
          </button>
        </div>
        <div className="flex sm:flex-row flex-col gap-2 mt-2 w-full">
          <Select
            value={selectedContractNumber?.toString()}
            onItemSelect={handleSelectContractNumber}
            className="flex-1"
            placeholder={t('contractNumber')}
            options={
              contractsQuery?.data?.map((el) => ({
                value: el.contractNumber,
                label: el.contractNumber,
              })) ?? []
            }
            leftSide={
              <span className="w-6 h-6 text-brand-brown">
                <Hash02 />
              </span>
            }
          />
          <Select
            className="flex-1"
            placeholder={t('status')}
            onItemSelect={handleSelectedStatusChange}
            value={selectedStatus?.toString()}
            options={ticketStatuses.map((status) => ({
              label: t(status.key),
              value: status.stageId.toString(),
            }))}
            leftSide={
              <span className="w-6 h-6 text-brand-brown">
                <CheckCircleBroken />
              </span>
            }
          />
        </div>
        <div className="mt-2">
          <Select
            options={ticketTypesQuery.data?.map((ticketType) => ({ label: ticketType.displayName, value: ticketType.id.toString() }))}
            className="flex-1"
            value={selectedTicketTypeId?.toString()}
            onItemSelect={handleSelectedTicketTypeIdChange}
            placeholder={t('requestTheme')}
            leftSide={
              <span className="w-6 h-6 text-brand-brown">
                <Loading02 />
              </span>
            }
          />
        </div>
        <div className="flex flex-col flex-1 gap-2 mt-10 overflow-y-auto">
          {ticketsQuery.isLoading ? (
            Array(3)
              .fill(null)
              .map((_, index) => <BaseSkeleton key={index} className="h-[88px]" />)
          ) : ticketsQuery.data?.length ? (
            ticketsQuery.data.map((ticket) => (
              <div
                onClick={() => handleTicketClick(ticket)}
                key={ticket.id}
                className={cn(
                  'flex justify-between border-2 border-gray-50 p-4 rounded-lg w-full h-[88px] cursor-pointer',
                  ticket.id === selectedTicket?.id ? 'bg-[#F9FAE0]' : 'bg-white hover:bg-gray-100',
                )}
              >
                <div className="flex flex-col w-full">
                  <div className="flex justify-between items-center w-full">
                    <span className="block flex-1 font-medium text-gray-900 text-xl overflow-hidden">
                      <span
                        className={`inline-block ${ticket.displayName.length > 20 ? 'animate-scroll' : ''}`}
                        style={{ willChange: 'transform' }}
                      >
                        {ticket.displayName}
                      </span>
                    </span>
                    <span className="font-normal text-[#B9B9B9] text-xs sm:text-sm">{dayjs(ticket.createDate).format('hh:mm')}</span>
                  </div>
                  <div className="flex justify-between items-center w-full">
                    <span className="font-normal text-[#595959] text-sm">
                      {t('contractNumber')} - #{ticket.xContractNumber[0]}
                    </span>
                    <span
                      style={{
                        backgroundColor: ticketStatuses.find((status) => status.stageId === ticket.stageId[0])?.color,
                      }}
                      className="flex items-center px-4 rounded-lg h-7 sm:h-8 text-white"
                    >
                      {t(ticketStatuses.find((status) => status.stageId === ticket.stageId[0])?.key)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col flex-1 justify-center items-center">
              <span className="bg-[#B9B9B9] px-4 py-2 rounded-md font-normal text-white text-xs">{t('noRequests')}</span>
            </div>
          )}
        </div>
      </div>
      {isTicketCreationModeEnabled ? (
        <CreateTicketForm onBackButtonClick={handleBackFromTicketFormClick} openTicketChat={handleTicketClick} />
      ) : selectedTicket ? (
        <Chat ticket={selectedTicket} onBackButtonClick={() => setSelectedTicket(null)} />
      ) : (
        <div className="flex flex-col flex-1">
          <div className="flex items-center bg-white mb-5 px-6 rounded-[32px] h-[82px]">
            <span className="font-semibold text-xl sm:text-2xl">{t('youMightBeLookingFor')}</span>
          </div>
          <div className="flex-1 bg-white p-6 rounded-t-[32px] overflow-y-auto">
            <div className="flex flex-wrap justify-between gap-5 mb-4">
              <div className="flex flex-col flex-1 border-[#F2F4F7] border-2 p-4 rounded-[8px]">
                <span className="text-[#505050]">
                  <IconSchedule size="sm" />
                </span>
                <span className="text-base sm:tex-xl my-4 font-medium">{t('rescheduleWasteCollection')}</span>
                <span className="font-[400] text-[#50505066]">{t('rescheduleDescription')}</span>
              </div>
              <div className="flex flex-col flex-1 border-[#F2F4F7] border-2 p-4 rounded-[8px]">
                <span className="text-[#505050]">
                  <IconContainer2 size="sm" />
                </span>
                <span className="text-base sm:tex-xl my-4 font-medium">{t('additionalWasteContainer')}</span>
                <span className="font-[400] text-[#50505066]">{t('additionalWasteContainerDescription')}</span>
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-5">
              <div className="flex flex-col flex-1 border-[#F2F4F7] border-2 p-4 rounded-[8px]">
                <span className="text-[#505050]">
                  <IconDamagedContainer size="sm" />
                </span>
                <span className="text-base sm:tex-xl my-4 font-medium">{t('containerIsDamaged')}</span>
                <span className="font-[400] text-[#50505066]">{t('containerDamagedDescription')}</span>
              </div>
              <div className="flex flex-col flex-1 border-[#F2F4F7] border-2 p-4 rounded-[8px]">
                <span className="text-[#505050]">
                  <IconXCircle size="sm" />
                </span>
                <span className="text-base sm:tex-xl my-4 font-medium">{t('cancelService')}</span>
                <span className="font-[400] text-[#50505066]">{t('cancelServiceDescription')}</span>
              </div>
            </div>
            <div className="mt-10">
              <span className="mb-4 font-bold text-2xl">{t('FAQ')}</span>
              {FAQ.map((el, index) => (
                <span className="flex py-[13px] font-medium text-xl" key={index}>
                  {el[currentLocale as 'et' | 'en' | 'ru']}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunicationPage;
